import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Copy, Download, Home, Menu, Mic2, Plus, Trash2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import BeatStrip from '../../components/booth/BeatStrip';
import BoothEditor from '../../components/booth/BoothEditor';
import CareCard from '../../components/booth/CareCard';
import FirstSongWelcome from '../../components/booth/FirstSongWelcome';
import GuidePanel from '../../components/booth/GuidePanel';
import PizzulMoves from '../../components/booth/PizzulMoves';
import PizzulTip from '../../components/reflect/PizzulTip';
import { careMatch, careTextForSong } from '../../utils/boothCare';
import { boothFetch } from '../../utils/boothClient';
import {
  BOOTH_OPEN_SONG_KEY,
  BOOTH_SONGS_KEY,
  BOOTH_SONG_WARNING_COUNT,
  VIBE_SEEDS,
  duplicateSong,
  loadSongs,
  mergeSongs,
  readStoredSongs,
  newSong,
  removeSong,
  renameSong,
  saveSongs,
  songToText,
} from '../../utils/boothSongs';
import { BOOTH_TIPS } from '../../utils/boothTips';
import {
  ensureLines,
  markGuide,
  finishGuide,
  nextStep,
  prevStep,
  shouldOfferGuide,
  startGuide,
  stepComplete,
} from '../../utils/boothGuide';
import { buildStyleString, claimTip, sheetReducer } from '../../utils/reflectEditor';
import { bpmFromStyle, styleChips } from '../../utils/reflectFlow';
import { vibeStyle } from '../../utils/vibeStyle';
import './TheBooth.css';

const TIP_KEY = 'sp_booth_tips';
const VIBE_LABELS = {
  'big-energy': 'Big Energy',
  'bounce-vibe': 'Bounce Vibe',
  'calm-focus': 'Calm Focus',
  'trap-energy': 'Trap Energy',
  'afro-chill': 'Afro Chill',
  'warm-rnb': 'Warm R&B',
  'dark-drill': 'Dark Drill',
  'latin-pop': 'Latin Pop',
  'latin-warm': 'Latin Warm',
  'reggaeton-bounce': 'Reggaeton Bounce',
  'playful-pop': 'Playful Pop',
  'confident-grind': 'Confident Grind',
};

const readTips = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(TIP_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

const downloadText = (text, filename, type = 'text/plain;charset=utf-8') => {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const safeFilename = (title) => (
  String(title || 'Untitled').trim().replace(/[^a-z0-9-_ ]/gi, '').replace(/\s+/g, '-') || 'Untitled'
);

const initialState = () => {
  const loaded = loadSongs(window.localStorage);
  let openId = null;
  try {
    openId = localStorage.getItem(BOOTH_OPEN_SONG_KEY);
  } catch (_error) {
    // The most recent song is still a useful fallback without local storage.
  }
  const selected = loaded.songs.find((song) => song.id === openId)
    || [...loaded.songs].sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)))[0]
    || null;
  return { ...loaded, activeId: selected?.id || null };
};

const TheBooth = () => {
  const loaded = useMemo(initialState, []);
  const [songs, setSongs] = useState(loaded.songs);
  const [activeId, setActiveId] = useState(loaded.activeId);
  const [recovered, setRecovered] = useState(loaded.recovered);
  const [creating, setCreating] = useState(!loaded.activeId);
  const [welcomeOpen, setWelcomeOpen] = useState(shouldOfferGuide(loaded.songs));
  const [guidePicking, setGuidePicking] = useState(false);
  const [beatPlaying, setBeatPlaying] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameText, setRenameText] = useState('');
  const [deletedSong, setDeletedSong] = useState(null);
  const [saveStatus, setSaveStatus] = useState('Saved');
  const [config, setConfig] = useState(null);
  const [careKind, setCareKind] = useState(null);
  const [tipQueue, setTipQueue] = useState([]);
  const lastSaved = useRef(JSON.stringify(loaded.songs));
  const seenTips = useRef(readTips());
  const deletedTimer = useRef(null);
  const guideRef = useRef(null);
  const shownCare = useRef(new Set());
  const songsRef = useRef(songs);
  const deletedIds = useRef(new Set());
  const activeSong = songs.find((song) => song.id === activeId) || null;
  const { color } = vibeStyle(activeSong?.vibe || '');
  const guideStep = activeSong?.guide && !activeSong.guide.done
    ? Math.max(1, Math.min(5, Number(activeSong.guide.step) || 1))
    : null;

  songsRef.current = songs;

  const showTip = useCallback((key, triggered = true) => {
    const claimed = claimTip(seenTips.current, key, triggered, BOOTH_TIPS);
    if (!claimed.next) return;
    seenTips.current = claimed.seen;
    try {
      localStorage.setItem(TIP_KEY, JSON.stringify(claimed.seen));
    } catch (_error) {
      // Tips remain once-per-visit when storage is unavailable.
    }
    setTipQueue((current) => [...current, claimed.next]);
  }, []);

  // The guide explains flow step by step, so the jargon tip waits for writers who skip it.
  useEffect(() => {
    showTip('first_open', !welcomeOpen && !guidePicking && !guideStep);
  }, [guidePicking, guideStep, showTip, welcomeOpen]);

  useEffect(() => {
    setBeatPlaying(false);
  }, [activeId]);

  useEffect(() => {
    let live = true;
    boothFetch('config')
      .then((data) => {
        if (live) setConfig(data);
      })
      .catch(() => {
        if (live) {
          setConfig({
            ai_available: false,
            ai_reason: 'resting',
            turnstile_site_key: '',
            care_phrases: { self_harm: [], harmed: [] },
          });
        }
      });
    return () => { live = false; };
  }, []);

  useEffect(() => () => clearTimeout(deletedTimer.current), []);

  const checkSongCare = useCallback((song) => {
    if (!song || !config?.care_phrases) return;
    const kind = careMatch(careTextForSong(song), config.care_phrases);
    if (!kind) return;
    const key = `${song.id}:${kind}`;
    if (shownCare.current.has(key)) return;
    shownCare.current.add(key);
    setCareKind(kind);
  }, [config?.care_phrases]);

  useEffect(() => {
    try {
      if (activeId) localStorage.setItem(BOOTH_OPEN_SONG_KEY, activeId);
      else localStorage.removeItem(BOOTH_OPEN_SONG_KEY);
    } catch (_error) {
      // An open song still works for this visit.
    }
  }, [activeId]);

  useEffect(() => {
    const serialized = JSON.stringify(songs);
    if (serialized === lastSaved.current) return undefined;
    setSaveStatus('Saving');
    const timer = window.setTimeout(() => {
      // Another tab may have saved since; merge so neither tab wipes the other's songs.
      const merged = mergeSongs(readStoredSongs(window.localStorage), songsRef.current, deletedIds.current);
      const result = saveSongs(window.localStorage, merged);
      checkSongCare(songsRef.current.find((song) => song.id === activeId));
      if (result.ok) {
        lastSaved.current = JSON.stringify(merged);
        if (JSON.stringify(songsRef.current) !== lastSaved.current) setSongs(merged);
        setSaveStatus('Saved');
      } else {
        setSaveStatus('Not saved. Storage is full.');
      }
    }, 800);
    return () => window.clearTimeout(timer);
  }, [activeId, checkSongCare, songs]);

  useEffect(() => {
    const pull = (event) => {
      if (event.key !== BOOTH_SONGS_KEY) return;
      const current = JSON.stringify(songsRef.current);
      const merged = mergeSongs(readStoredSongs(window.localStorage), songsRef.current, deletedIds.current);
      const serialized = JSON.stringify(merged);
      if (serialized === current) return;
      if (current === lastSaved.current) lastSaved.current = serialized;
      setSongs(merged);
    };
    window.addEventListener('storage', pull);
    return () => window.removeEventListener('storage', pull);
  }, []);

  useEffect(() => {
    const warn = (event) => {
      if (saveStatus === 'Saved') return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [saveStatus]);

  const updateSong = useCallback((id, updater) => {
    setSongs((current) => current.map((song) => {
      if (song.id !== id) return song;
      const updated = updater(song);
      return { ...updated, updated_at: new Date().toISOString() };
    }));
  }, []);

  const dispatch = useCallback((action) => {
    if (!activeId) return;
    updateSong(activeId, (song) => sheetReducer(song, action));
  }, [activeId, updateSong]);

  const createSong = (vibe) => {
    const base = newSong(vibe);
    const song = guidePicking ? startGuide(base) : base;
    setSongs((current) => [...current, song]);
    setActiveId(song.id);
    setCreating(false);
    setWelcomeOpen(false);
    setGuidePicking(false);
    setDrawerOpen(false);
  };

  const prepareGuideStep = useCallback((song, mover) => {
    const moved = mover(song);
    if (moved.guide.step === 3) return ensureLines(moved, 'Hook', 2);
    return moved.guide.step === 4 ? ensureLines(moved, 'Verse 1', 4) : moved;
  }, []);

  const advanceGuide = useCallback(() => {
    if (!activeId) return;
    updateSong(activeId, (song) => prepareGuideStep(song, nextStep));
  }, [activeId, prepareGuideStep, updateSong]);

  const goBackGuide = useCallback(() => {
    if (!activeId) return;
    updateSong(activeId, (song) => prepareGuideStep(song, prevStep));
  }, [activeId, prepareGuideStep, updateSong]);

  const exitGuide = useCallback(() => {
    if (!activeId) return;
    updateSong(activeId, finishGuide);
  }, [activeId, updateSong]);

  const keepWriting = useCallback(() => {
    if (!activeId) return;
    updateSong(activeId, (song) => {
      const withVerse = ensureLines(song, 'Verse 2', 1);
      return finishGuide(ensureLines(withVerse, 'Outro', 1));
    });
    showTip('keep_writing');
  }, [activeId, showTip, updateSong]);

  const markGuideFlag = useCallback((flag) => {
    if (!activeId) return;
    setSongs((current) => current.map((song) => {
      if (song.id !== activeId) return song;
      const marked = markGuide(song, flag);
      return marked === song ? song : { ...marked, updated_at: new Date().toISOString() };
    }));
  }, [activeId]);

  const handlePlayingChange = useCallback((playing) => {
    setBeatPlaying(playing);
    if (playing) markGuideFlag('beatPlayed');
  }, [markGuideFlag]);

  // Beat, flow and finish happen in the guide panel itself, so bring it back into view;
  // the hook and verse steps scroll to their section instead (BoothEditor).
  useEffect(() => {
    if (![1, 2, 5].includes(guideStep) || !guideRef.current) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    guideRef.current.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }, [guideStep]);

  // Writing steps start with the slots they ask for: 2 hook lines, 4 verse lines.
  useEffect(() => {
    if (!activeId || (guideStep !== 3 && guideStep !== 4)) return;
    const [label, count] = guideStep === 3 ? ['Hook', 2] : ['Verse 1', 4];
    const section = activeSong?.sections?.find((item) => item.label === label);
    if ((section?.lines || []).length >= count) return;
    updateSong(activeId, (song) => ensureLines(song, label, count));
  }, [activeId, activeSong?.sections, guideStep, updateSong]);

  const deleteSong = (song) => {
    const index = songs.findIndex((item) => item.id === song.id);
    deletedIds.current.add(song.id);
    setSongs((current) => removeSong(current, song.id));
    if (activeId === song.id) {
      const remaining = songs.filter((item) => item.id !== song.id);
      setActiveId(remaining[0]?.id || null);
      if (!remaining.length) setCreating(true);
    }
    setDeletedSong({ song, index });
    clearTimeout(deletedTimer.current);
    deletedTimer.current = window.setTimeout(() => setDeletedSong(null), 6000);
  };

  const undoDelete = () => {
    if (!deletedSong) return;
    deletedIds.current.delete(deletedSong.song.id);
    setSongs((current) => {
      const next = [...current];
      next.splice(Math.min(deletedSong.index, next.length), 0, deletedSong.song);
      return next;
    });
    setActiveId(deletedSong.song.id);
    setCreating(false);
    setDeletedSong(null);
    clearTimeout(deletedTimer.current);
  };

  const duplicate = (song) => {
    const copy = duplicateSong(song);
    setSongs((current) => [...current, copy]);
    setActiveId(copy.id);
    setCreating(false);
    setDrawerOpen(false);
  };

  const finishRename = (song) => {
    updateSong(song.id, (current) => renameSong(current, renameText));
    setRenamingId(null);
  };

  const requestRhymes = useCallback(async (word, words) => {
    return boothFetch('rhymes', {
      method: 'POST',
      body: { word, words },
    });
  }, []);

  const downloadSong = (song = activeSong) => {
    if (!song) return;
    downloadText(songToText(song), `${safeFilename(song.title)}.txt`);
  };

  const changeBpm = useCallback((nextBpm) => {
    if (!activeId) return;
    updateSong(activeId, (song) => ({
      ...song,
      style: buildStyleString(styleChips(song.style), nextBpm, false),
    }));
  }, [activeId, updateSong]);

  const changeBeat = useCallback((beat) => {
    if (!activeId) return;
    updateSong(activeId, (song) => ({ ...song, beat }));
  }, [activeId, updateSong]);

  return (
    <div className="booth-page min-h-screen text-white" style={{ '--glow': color }}>
      <div className="booth-orb booth-orb-one" aria-hidden="true" />
      <div className="booth-orb booth-orb-two" aria-hidden="true" />
      <header className="booth-topbar sticky top-0 z-40 border-b border-white/10">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-3 py-3 sm:px-5">
          <Link to="/booth" className="mr-auto flex min-h-11 items-center gap-2 rounded-full px-2">
            <Mic2 className="h-5 w-5" style={{ color }} />
            <span className="font-heading text-xl font-black sm:text-2xl">The Booth</span>
          </Link>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="flex min-h-11 items-center gap-2 rounded-full border border-white/15 px-3
              text-sm font-black hover:border-[color:var(--glow)]"
          >
            <Menu className="h-4 w-4" /> My songs
          </button>
          <button
            type="button"
            onClick={() => {
              setWelcomeOpen(false);
              setGuidePicking(false);
              setCreating(true);
            }}
            className="neon-btn flex min-h-11 items-center gap-2 rounded-full px-3 text-sm
              font-black text-black"
            style={{ backgroundColor: color }}
          >
            <Plus className="h-4 w-4" /> New song
          </button>
          <Link
            to="/"
            className="flex min-h-11 items-center gap-2 rounded-full px-2 text-sm font-bold
              text-white/60 hover:text-white"
          >
            <Home className="h-4 w-4" />
            <span className="hidden sm:inline">Studio Playground</span>
          </Link>
        </div>
      </header>

      <div className="booth-screen mx-auto max-w-7xl px-3 py-6 sm:px-5">
        {recovered !== null && (
          <div className="booth-editor-control mb-5 flex flex-wrap items-center gap-3 rounded-2xl border
            border-[#FFB347]/50 bg-[#FFB347]/10 p-4"
          >
            <p className="min-w-0 flex-1 text-base font-bold">
              Your saved song data could not be read. A fresh song list is ready.
            </p>
            <button
              type="button"
              onClick={() => downloadText(recovered, 'booth-recovered-data.txt')}
              className="min-h-11 rounded-full border border-[#FFB347]/60 px-4 font-black"
            >
              Download recovered data
            </button>
            <button
              type="button"
              onClick={() => setRecovered(null)}
              className="flex min-h-11 min-w-11 items-center justify-center rounded-full"
              aria-label="Dismiss recovered data notice"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {activeSong && !creating && (
          <>
            <div className="booth-editor-control mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-black uppercase tracking-[0.18em]" style={{ color }}>
                Song in this browser
              </p>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <span
                  role="status"
                  aria-live="polite"
                  className={`rounded-full border px-3 py-1.5 text-sm font-black ${
                    saveStatus === 'Saved'
                      ? 'border-white/10 text-white/45'
                      : saveStatus === 'Saving'
                        ? 'border-white/20 text-white/70'
                        : 'border-[#FFB19C]/50 text-[#FFB19C]'
                  }`}
                >
                  {saveStatus}
                </span>
                {saveStatus === 'Not saved. Storage is full.' && (
                  <>
                    <button
                      type="button"
                      onClick={() => downloadSong()}
                      className="min-h-11 rounded-full border border-white/15 px-3 text-sm font-black"
                    >
                      Download this song
                    </button>
                    <button
                      type="button"
                      onClick={() => setDrawerOpen(true)}
                      className="min-h-11 rounded-full border border-white/15 px-3 text-sm font-black"
                    >
                      Manage songs
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className={guideStep === 1 ? 'guide-beat-highlight' : ''}>
              <BeatStrip
                key={activeSong.id}
                color={color}
                song={activeSong}
                onChangeBeat={changeBeat}
                onChangeBpm={changeBpm}
                onFirstPlay={() => showTip('first_beat')}
                onPlayingChange={handlePlayingChange}
              />
            </div>
            {guideStep && (
              <div ref={guideRef} className="guide-anchor">
                <GuidePanel
                  aiAvailable={Boolean(config?.ai_available)}
                  color={color}
                  complete={stepComplete(activeSong, guideStep, {
                    beatPlayed: false,
                    flowConfirmed: false,
                  })}
                  onBack={goBackGuide}
                  onDownload={() => downloadSong()}
                  onExit={exitGuide}
                  onFlowConfirmed={() => markGuideFlag('flowConfirmed')}
                  onKeepWriting={keepWriting}
                  onNext={advanceGuide}
                  onNoSound={() => markGuideFlag('beatPlayed')}
                  onSkip={advanceGuide}
                  playing={beatPlaying}
                  song={activeSong}
                  step={guideStep}
                  bpm={bpmFromStyle(activeSong.style) || 90}
                />
              </div>
            )}
            <BoothEditor
              PizzulMoves={PizzulMoves}
              sheet={activeSong}
              guideStep={guideStep}
              guideCallout={guideStep === 3 || guideStep === 4 ? {
                aiAvailable: Boolean(config?.ai_available),
                complete: stepComplete(activeSong, guideStep),
                onNext: advanceGuide,
              } : null}
              dispatch={dispatch}
              requestRhymes={requestRhymes}
              onDownload={() => downloadSong()}
              onTip={showTip}
              pizzulMovesProps={{
                config,
                onCare: setCareKind,
              }}
            />
          </>
        )}

        {welcomeOpen && shouldOfferGuide(songs) && (
          <FirstSongWelcome
            color={color}
            onGuided={() => {
              setWelcomeOpen(false);
              setGuidePicking(true);
              setCreating(true);
            }}
            onStandard={() => {
              setWelcomeOpen(false);
              setGuidePicking(false);
              setCreating(true);
            }}
          />
        )}

        {creating && !welcomeOpen && (
          <section className="mx-auto max-w-5xl py-8 sm:py-14">
            <p className="text-sm font-black uppercase tracking-[0.2em]" style={{ color }}>
              {guidePicking ? 'First Song guide' : 'New song'}
            </p>
            <h1 className="mt-2 font-heading text-4xl font-black sm:text-6xl">Pick the energy.</h1>
            <p className="mt-3 max-w-2xl text-lg text-white/60">
              {guidePicking
                ? 'Every song starts with a feel. Pick the one that sounds like your mood today.'
                : 'This sets the first style and tempo. You can change both inside the song.'}
            </p>
            <div className="vibe-row mt-8 flex gap-3 overflow-x-auto pb-4">
              {Object.keys(VIBE_SEEDS).map((id) => {
                const { Icon, color: vibeColor } = vibeStyle(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => createSong(id)}
                    className="vibe-card min-h-36 w-48 shrink-0 rounded-2xl border p-4 text-left
                      transition-transform"
                    style={{ '--vibe-color': vibeColor }}
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-full"
                      style={{ color: vibeColor, backgroundColor: `${vibeColor}1f` }}
                    >
                      <Icon className="h-6 w-6" />
                    </span>
                    <span className="mt-5 block font-heading text-xl font-black">
                      {VIBE_LABELS[id]}
                    </span>
                    <span className="mt-1 block text-sm font-bold text-white/50">
                      {VIBE_SEEDS[id].bpm} BPM
                    </span>
                  </button>
                );
              })}
            </div>
            {!guidePicking && (
              <button
                type="button"
                onClick={() => createSong(null)}
                className="mt-5 min-h-12 rounded-full border border-white/20 px-6 text-base
                  font-black hover:border-[color:var(--glow)]"
              >
                Skip, just write
              </button>
            )}
            {songs.length > 0 && !guidePicking && (
              <button
                type="button"
                onClick={() => setGuidePicking(true)}
                className="mt-5 min-h-12 rounded-full px-4 text-base font-bold text-white/55
                  hover:text-white"
              >
                Walk me through it
              </button>
            )}
            {activeSong && (
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setGuidePicking(false);
                }}
                className="ml-2 min-h-12 rounded-full px-5 text-base font-black text-white/55"
              >
                Cancel
              </button>
            )}
          </section>
        )}
      </div>

      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/70"
          role="dialog"
          aria-modal="true"
          aria-label="My songs"
        >
          <aside className="h-full w-full max-w-md overflow-y-auto border-l border-white/10
            bg-[#090b0c] p-4 shadow-2xl sm:p-6"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em]" style={{ color }}>
                  Saved here
                </p>
                <h2 className="font-heading text-3xl font-black">My songs</h2>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex min-h-11 min-w-11 items-center justify-center rounded-full
                  hover:bg-white/10"
                aria-label="Close My songs"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {songs.length >= BOOTH_SONG_WARNING_COUNT && (
              <p className="mt-5 rounded-2xl border border-[#FFB347]/40 bg-[#FFB347]/10 p-4
                text-base font-bold"
              >
                You've got a lot of songs here. Download the ones you're done with.
              </p>
            )}

            <button
              type="button"
              onClick={() => {
                setWelcomeOpen(false);
                setGuidePicking(false);
                setCreating(true);
                setDrawerOpen(false);
              }}
              className="neon-btn mt-5 flex min-h-12 w-full items-center justify-center gap-2
                rounded-full font-black text-black"
              style={{ backgroundColor: color }}
            >
              <Plus className="h-5 w-5" /> New song
            </button>

            <div className="mt-5 space-y-3">
              {[...songs]
                .sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)))
                .map((song) => (
                  <article
                    key={song.id}
                    className={`rounded-2xl border p-3 ${song.id === activeId
                      ? 'border-[color:var(--glow)] bg-white/[0.06]' : 'border-white/10'}`}
                  >
                    {renamingId === song.id ? (
                      <form
                        onSubmit={(event) => {
                          event.preventDefault();
                          finishRename(song);
                        }}
                        className="flex gap-2"
                      >
                        <input
                          value={renameText}
                          onChange={(event) => setRenameText(event.target.value)}
                          maxLength={80}
                          autoFocus
                          className="min-h-11 min-w-0 flex-1 rounded-xl border border-white/20
                            bg-black/30 px-3 text-base outline-none"
                          aria-label="Rename song"
                        />
                        <button type="submit" className="min-h-11 rounded-full px-3 font-black">
                          Save
                        </button>
                      </form>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setActiveId(song.id);
                          setCreating(false);
                          setDrawerOpen(false);
                        }}
                        className="flex min-h-11 w-full items-center text-left"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-heading text-xl font-black">
                            {song.title}
                          </span>
                          <span className="block truncate text-sm text-white/45">{song.style}</span>
                        </span>
                        <ChevronRight className="h-5 w-5 text-white/35" />
                      </button>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1 border-t border-white/10 pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setRenamingId(song.id);
                          setRenameText(song.title);
                        }}
                        className="min-h-11 rounded-full px-3 text-sm font-bold text-white/60"
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        onClick={() => duplicate(song)}
                        className="flex min-h-11 items-center gap-1 rounded-full px-3 text-sm
                          font-bold text-white/60"
                      >
                        <Copy className="h-4 w-4" /> Duplicate
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadSong(song)}
                        className="flex min-h-11 items-center gap-1 rounded-full px-3 text-sm
                          font-bold text-white/60"
                      >
                        <Download className="h-4 w-4" /> Download
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteSong(song)}
                        className="ml-auto flex min-h-11 min-w-11 items-center justify-center
                          rounded-full text-white/45 hover:bg-red-500/10 hover:text-red-200"
                        aria-label={`Delete ${song.title}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </article>
                ))}
            </div>
          </aside>
        </div>
      )}

      {deletedSong && (
        <div className="fixed bottom-4 left-3 z-[60] flex min-h-12 items-center gap-3
          rounded-full border border-white/15 bg-[#111415] px-4 text-base font-bold shadow-2xl"
        >
          Song deleted
          <button
            type="button"
            onClick={undoDelete}
            className="min-h-11 rounded-full px-3 font-black"
            style={{ color }}
          >
            Undo
          </button>
        </div>
      )}

      <PizzulTip
        tip={tipQueue[0] ? BOOTH_TIPS[tipQueue[0]] : null}
        onDismiss={() => setTipQueue((current) => current.slice(1))}
      />
      <CareCard kind={careKind} onClose={() => setCareKind(null)} />
    </div>
  );
};

export default TheBooth;
